<template>
	<div :class="{ 'is-error': isError }" class="container">
		<Breadcrumbs
			v-if="!isError && !isSmallDevice"
			:items="breadcrumb"
			@onBreadcrumbClick="onBreadcrumbClick"
		/>
		<LevelNav v-if="!isError" :nav-counter="navCounter" />
		<NotFoundContent v-if="isError" />
		<LoadingSpinner v-else-if="isLoading" />
		<div v-else-if="levelInfo" class="content full-height">
			<Board :board="levelInfo.board" :color="packInfo.color" />
			<div v-if="isSmallDevice" class="advertising-container">
				<Advertising :stub="AD_LAYOUT_STUBS.MOB_IN_CONTENT" />
			</div>
			<div class="info">
				<BoardCard
					v-if="levelInfo.bonus_words.length"
					:title="$t('WordscapesLevelPageBonusTableHeading')"
					:color="packInfo.color"
					:words="levelInfo.bonus_words"
					:is-bonus-words="true"
				/>
				<BoardCard
					class="card--lower"
					:title="wordsTitle"
					:color="packInfo.color"
					:words="levelInfo.words"
				/>
				<p class="text" v-html="levelText" />
			</div>
		</div>
		<LevelNav v-if="!isError && isSmallDevice" :nav-counter="navCounter" />
		<aside v-if="!isError && !isSmallDevice" class="sidebar">
			<Advertising
				:stub="AD_LAYOUT_STUBS.RIGHT_RAIL"
				:style="{ top: `${topElementsHeight + 25}px` }"
				class="advertising--sticky"
			/>
		</aside>
	</div>
</template>

<script>
import { AD_LAYOUT_STUBS } from "@/common/consts";
const Advertising = () =>
	import(
		/* webpackChunkName: "component-advertising" */ "@/components/advertising"
	);
const Breadcrumbs = () =>
	import(
		/* webpackChunkName: "component-breadcrumbs" */ "@/components/breadcrumbs"
	);
const Board = () =>
	import(/* webpackChunkName: "component-board" */ "@/components/board");
const BoardCard = () =>
	import(
		/* webpackChunkName: "component-board-card" */ "@/components/board-card"
	);
const LevelNav = () =>
	import(
		/* webpackChunkName: "component-level-nav" */ "@/components/level-nav"
	);
const LoadingSpinner = () =>
	import(
		/* webpackChunkName: "component-loading-spinner" */ "@/components/loading-spinner"
	);
import NotFoundContent from "@/pages/not-found/content.vue";

import { getLevelInfo, getPackInfo } from "@/common/services/search";
import metaInfo from "./meta";
import { mapGetters } from "vuex";

export default {
	name: "LevelPage",
	components: {
		Advertising,
		Breadcrumbs,
		Board,
		BoardCard,
		LevelNav,
		LoadingSpinner,
		NotFoundContent,
	},
	data: function() {
		return {
			AD_LAYOUT_STUBS,
			levelInfo: null,
			navCounter: +this.$route.params.level,
			isLoading: false,
			isError: false,
		};
	},
	computed: {
		...mapGetters(["topElementsHeight"]),
		packInfo() {
			return getPackInfo(
				this.$route.params.group,
				this.$route.params.pack
			);
		},
		wordsTitle() {
			return this.$t("WordscapesLevelPageResultsTableHeading", {
				NumberOfWords: this.levelInfo.words.length,
				PackName: this.levelInfo.stage,
				LevelNumber: this.$route.params.level,
			});
		},
		levelText() {
			const words = [...(this.levelInfo?.words || [])]
				.map((w) => w.toLowerCase())
				.sort();
			let wordsText = "";

			if (words.length === 1) {
				wordsText = words[0];
			} else if (words.length === 2) {
				wordsText = words.join(" and ");
			} else if (words.length > 2) {
				const lastWord = words[words.length - 1];
				words[words.length - 1] = `and ${lastWord}`;
				wordsText = words.join(", ");
			}

			return `The Answers for Wordscapes Level ${this.$route.params.level} from the ${this.levelInfo.stage} pack and ${this.levelInfo.world} group are: ${wordsText}.`;
		},
		breadcrumb() {
			return [
				{
					label: this.$t("BreadcrumbWordscapesGroupPageText", {
						GroupName: this.$route.params.group,
					}),
					url: this.$t("BreadcrumbWordscapesGroupPageUrl", {
						GroupName: this.$route.params.group,
					}),
					type: "Group",
				},
				{
					label: this.$t("BreadcrumbWordscapesPackPageText", {
						PackName: this.$route.params.pack,
					}),
					url: this.$t("BreadcrumbWordscapesPackPageUrl", {
						GroupName: this.$route.params.group,
						PackName: this.$route.params.pack,
					}),
					type: "Pack",
				},
				{
					label: this.$t("BreadcrumbWordscapesLevelText", {
						LevelNumber: this.$route.params.level,
					}),
				},
			];
		},
	},
	watch: {
		$route: "refreshPage", // call again the method if the route changes
	},
	mounted: function() {
		this.refreshPage();
	},
	methods: {
		async refreshPage() {
			this.isError = false;
			this.isLoading = true;
			this.navCounter = +this.$route.params.level;
			const levelInfo = await getLevelInfo(
				this.$route.params.level,
				this.$store
			);

			this.isLoading = false;
			if (!levelInfo) {
				this.isError = true;
				return;
			}

			this.levelInfo = levelInfo;
			this.pushRouteParams();
		},
		pushRouteParams() {
			const isSameGroup =
				this.$route.params.group === this.levelInfo.world.toLowerCase();
			const isSamePack =
				this.$route.params.pack === this.levelInfo.stage.toLowerCase();

			if (isSameGroup && isSamePack) return;

			this.$router
				.replace({
					params: {
						group: this.levelInfo.world.toLowerCase(),
						pack: this.levelInfo.stage.toLowerCase(),
						level: `${this.navCounter}`,
					},
				})
				.catch((err) => {
					// Catch the exception produced when the trailin slash is added to the new path
					// (e.g. /answers/high%20seas/crest/5378 to /answers/high%20seas/crest/5378/)
					// See: https://github.com/vuejs/vue-router/issues/2881#issuecomment-520554378
				});
		},
		onBreadcrumbClick(item) {
			const eventAction = `Click on ${item.type} breadcrumb ${this.$t(
				`BreadcrumbWordscapes${item.type}PageText`,
				{
					[`${item.type}Name`]: item.label,
				}
			)}`;

			this.$store.dispatch("trackEvent", {
				eventCategory: "Breadcrumb",
				eventAction,
			});
		},
	},
	metaInfo,
};
</script>

<style lang="scss" src="./styles.scss" scoped></style>
